import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  ListGroup,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaTrashAlt, FaMinus, FaPlus, FaArrowLeft } from "react-icons/fa";
import { connect } from "react-redux";
import {
  updateCart,
  removeFromCart,
  clearCart,
} from "../redux/actions/cartActions";
import { orderAPI } from "../utils/api";
import { Bootpay } from "@bootpay/client-js";

const CartPage = ({ cartItems, updateCart, removeFromCart, clearCart }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState({}); // userData state 추가
  const [prayType, setPrayType] = useState("가족화목"); // 기도 종류 기본값
  const [userComment, setUserComment] = useState(""); // 기도 받는 사람 정보
  const [prayDate, setPrayDate] = useState(new Date()); // 기도 날짜

  // 컴포넌트 마운트 시 장바구니 정보 불러오기 및 사용자 정보 가져오기
  useEffect(() => {
    try {
      const items = JSON.parse(localStorage.getItem("cart") || "[]");
      updateCart(items);

      // 로그인 상태 확인
      const token = localStorage.getItem("accessToken");
      setIsAuthenticated(!!token);

      // 사용자 정보 가져오기 (로그인된 경우)
      if (token) {
        const username = localStorage.getItem("username") || "";
        // 실제 구현 시 API를 통해 사용자 이메일 등 상세 정보를 가져와야 함
        setUserData({
          username: username,
          email: "", // API로부터 가져와야 함
          phone: "", // API로부터 가져와야 함
        });
      }
    } catch (error) {
      console.error("장바구니 데이터 로드 실패:", error);
      setError("장바구니 정보를 불러오는데 실패했습니다.");
    }
  }, [updateCart]);

  // 수량 변경 핸들러
  const handleQuantityChange = (index, change) => {
    const updatedCart = [...cartItems];
    const newQty = Math.max(1, updatedCart[index].quantity + change);
    updatedCart[index].quantity = newQty;
    updateCart(updatedCart);
  };

  // 아이템 삭제 핸들러
  const handleRemoveItem = (index) => {
    removeFromCart(index);
  };

  // 장바구니 비우기 핸들러
  const handleClearCart = () => {
    if (window.confirm("장바구니를 비우시겠습니까?")) {
      clearCart();
    }
  };



  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  // 결제 처리 핸들러 수정
  const handleCheckout = async () => {
    // async 키워드 추가
    if (!isAuthenticated) {
      if (
        window.confirm("로그인이 필요합니다. 로그인 페이지로 이동하시겠습니까?")
      ) {
        navigate("/login", { state: { returnUrl: "/cart" } });
      }
      return;
    }
    if (!agreeTerms) {
      setError("결제 진행을 위해 약관에 동의해주세요.");
      return;
    }
    if (cartItems.length === 0) {
      setError("장바구니에 상품이 없습니다.");
      return;
    }

    setLoading(true);
    setError(null); // 이전 오류 메시지 초기화

    const orderName =
      cartItems.length > 1
        ? `${cartItems[0].name} 외 ${cartItems.length - 1}건`
        : cartItems[0].name;

    const bootpayItems = cartItems.map((item) => ({
      name: item.name, // Bootpay 스키마에 맞게 'item_name' -> 'name'
      qty: item.quantity,
      id: item.id, // Bootpay 스키마에 맞게 'unique' -> 'id'
      price: item.price,
      // cat1, cat2 등은 Bootpay 최신 스키마 확인 필요
    }));

    try {
      // @bootpay/client-js 사용 방식에 따라 requestPayment 호출
      const response = await Bootpay.requestPayment({
        // await 키워드 추가
        application_id: "6167c2cc7b5ba4002152c51f", // 실제 웹 Application ID로 변경
        price: calculateTotal(),
        order_name: orderName,
        order_id: `ORDER_${new Date().getTime()}`,
        pg: convertPaymentMethod(paymentMethod), // PG사 설정
        method: paymentMethod, // 결제수단
        user: {
          // user_info -> user
          username: userData.username || "사용자",
          email: userData.email || "",
          phone: userData.phone || "",
        },
        items: bootpayItems,
        extra: {
          theme: "purple", // 테마 설정은 최신 SDK에서 지원 여부 확인
          show_close_button: false,
          open_type: "iframe", // 웹에서 iframe 사용 시
        },
        // show_agree_window 옵션은 @bootpay/client-js에서 다르게 처리될 수 있음, 문서 확인 필요
      });

      // Bootpay 이벤트 핸들러 방식이 변경되었을 수 있습니다.
      // .error, .cancel, .ready, .confirm, .done 대신,
      // await Bootpay.requestPayment(...)의 반환값(response)이나
      // 별도의 이벤트 리스너 등록 방식으로 처리할 수 있습니다.
      // 아래는 일반적인 Promise 기반 처리 예시입니다.

      // response.event에 따라 분기 처리 (예시, 실제 Bootpay 응답 구조 확인 필요)
      switch (response.event) {
        case "done": // 성공
          console.log("결제 완료:", response.data);
          setLoading(false);

          // cart_bloc.toJson() 형식에 맞춰 주문 데이터 구성
          const orderData = {
            // 사찰 ID
            store: cartItems[0]?.templeId || "",
          
            // 주문 상품 목록 (각 항목에 item, count, options 필드만 포함)
            items: cartItems.map((item) => ({
              item: item.id,         // 상품 ID
              count: item.quantity,  // 수량
              options: item.options && item.options.length > 0
                ? item.options.map((opt) => ({
                    option: opt.id   // 옵션 ID
                  }))
                : []                 // 옵션이 없는 경우 빈 배열
            })),
          
            // 주문 날짜 (ISO 형식, UTC 기준)
            date: new Date(prayDate).toISOString(),
          
            // 주문 상태 (모바일과 웹의 차이 고려)
            status: "PREPARE",  // 웹에서는 바로 PAID로 설정 (모바일은 PREPARE)
          
            // 총 금액
            price: calculateTotal(),
          
            // 사용자 입력 정보
            user_comment: userComment,  // 기도 받는 사람 정보
            pray_type: prayType,        // 기도 종류
          
            // 결제 영수증 ID
            receipt_id: response.data?.receipt_id || ""
          };

          try {
            console.log("주문 데이터:", orderData);

            // 서버 API 호출하여 주문 생성 (orderAPI.create 함수 사용)
            const createResponse = await orderAPI.create(orderData);

            // 응답에서 주문 ID 추출 (mobile의 payment.dart 참고)
            const orderId = createResponse.data.id;
            console.log("주문 생성 완료, 주문 ID:", orderId);

            // 로컬 스토리지에 주문 정보 저장 (필요시)
            localStorage.setItem(
              "latestOrder",
              JSON.stringify({
                ...orderData,
                id: orderId,
                orderedAt: new Date().toISOString(),
              })
            );

            // 장바구니 비우기
            clearCart();

            // 주문 완료 페이지로 이동 (mobile의 Routes.paymentDone 와 유사)
            navigate("/order-success", { state: { orderId } });
          } catch (error) {
            console.error("주문 생성 API 오류:", error);
            setError("주문 정보 저장 중 오류가 발생했습니다.");
          }
          break;
        case "cancel": // 취소
          console.log("결제 취소:", response.data);
          setLoading(false);
          alert("결제가 취소되었습니다.");
          break;
        case "error": // 오류
          console.error("결제 오류:", response.data);
          setError(
            `결제 처리 중 오류가 발생했습니다: ${
              response.data?.message || "알 수 없는 오류"
            }`
          );
          setLoading(false);
          break;
        default:
          // 기타 이벤트 처리 (ex: issued, confirm 등)
          console.log("Bootpay event:", response.event, response.data);
          setLoading(false); // 예기치 않은 상황에 대한 로딩 해제
          break;
      }
    } catch (e) {
      console.error("Bootpay 요청 중 예외 발생:", e);
      setError("결제 시스템 연동 중 오류가 발생했습니다.");
      setLoading(false);
    }
  };
  // 결제 수단을 BootPay PG 코드로 변환
  const convertPaymentMethod = (method) => {
    // 항상 "페이레터"를 사용하도록 변경
    return "페이레터";
    /* 기존 로직 주석 처리
    switch (method) {
      case "card":
        return "inicis"; // 이니시스
      case "bank":
        return "inicis";
      case "kakao":
        return "kakao";
      case "naver": // Flutter에서는 'nice_v2'를 사용하나, 웹에서는 'naverpay' PG 코드가 있을 수 있습니다. Bootpay 문서 확인 필요.
        return "nice_v2"; // 또는 Bootpay에서 네이버페이에 해당하는 정확한 PG 코드
      default:
        return "inicis";
    }
    */
  };
  // 금액 포맷 함수
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ko-KR").format(amount);
  };

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <h1 className="h2 fw-bold mb-4">장바구니</h1>
          <Button
            as={Link}
            to="/pray"
            variant="link"
            className="p-0 mb-4 text-decoration-none"
            style={{ color: "var(--main-color)" }}
          >
            <FaArrowLeft className="me-2" /> 기도 상품 더 보기
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={8} className="mb-4">
          {cartItems.length === 0 ? (
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Body className="text-center py-5">
                <h3 className="h5 mb-4 text-muted">장바구니가 비어있습니다.</h3>
                <Button
                  as={Link}
                  to="/pray"
                  variant="primary"
                  style={{
                    backgroundColor: "var(--main-color)",
                    borderColor: "var(--main-color)",
                    borderRadius: "100px",
                  }}
                >
                  기도 상품 보러가기
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <Card className="shadow-sm border-0 rounded-4">
              <Card.Header className="bg-white py-3 border-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="h5 mb-0">주문 상품 ({cartItems.length})</h2>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleClearCart}
                    style={{ borderRadius: "100px" }}
                  >
                    장바구니 비우기
                  </Button>
                </div>
              </Card.Header>
              <ListGroup variant="flush">
                {cartItems.map((item, index) => (
                  <ListGroup.Item key={index} className="py-3 border-bottom">
                    <div className="d-flex">
                      {/* 상품 이미지 */}
                      {item.image && (
                        <div
                          className="me-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            backgroundImage: `url(${item.image})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            flexShrink: 0,
                          }}
                        />
                      )}

                      {/* 상품 정보 */}
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h3 className="h6 mb-1">{item.name}</h3>
                            <p className="text-muted small mb-2">
                              {item.templeName}
                            </p>
                          </div>
                          <Button
                            variant="link"
                            className="p-0 text-danger"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <FaTrashAlt size={14} />
                          </Button>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <div className="d-flex align-items-center">
                            <Button
                              variant="light"
                              size="sm"
                              className="d-flex align-items-center justify-content-center p-0"
                              onClick={() => handleQuantityChange(index, -1)}
                              style={{
                                width: "26px",
                                height: "26px",
                                borderRadius: "50%",
                              }}
                            >
                              <FaMinus size={10} />
                            </Button>
                            <span className="mx-3">{item.quantity}</span>
                            <Button
                              variant="light"
                              size="sm"
                              className="d-flex align-items-center justify-content-center p-0"
                              onClick={() => handleQuantityChange(index, 1)}
                              style={{
                                width: "26px",
                                height: "26px",
                                borderRadius: "50%",
                              }}
                            >
                              <FaPlus size={10} />
                            </Button>
                          </div>
                          <div className="text-end">
                            <p
                              className="mb-0 fw-bold"
                              style={{ color: "var(--main-color)" }}
                            >
                              {formatCurrency(item.price * item.quantity)}원
                            </p>
                            <small className="text-muted">
                              {formatCurrency(item.price)}원 × {item.quantity}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          {/* 주문 요약 */}
          <Card
            className="shadow-sm border-0 rounded-4 mb-4 sticky-lg-top"
            style={{ top: "20px" }}
          >
            <Card.Header className="bg-white py-3 border-0">
              <h2 className="h5 mb-0">결제 정보</h2>
            </Card.Header>
            <Card.Body>


              {/* 결제수단 선택 부분에 부트페이 지원 결제 수단으로 변경 */}
              <div className="mb-4">
                <label className="form-label">결제 수단</label>
                <Form.Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{ borderRadius: "100px" }}
                >
                  <option value="card">신용카드</option>
                  <option value="bank">계좌이체</option>
                  <option value="vbank">가상계좌</option>
                  <option value="phone">휴대폰결제</option>
                  <option value="kakao">카카오페이</option>
                  <option value="naverpay">네이버페이</option>
                  <option value="payco">페이코</option>
                  <option value="toss">토스</option>
                </Form.Select>
              </div>

              {/* 기도 종류 선택 */}
              <div className="mb-4">
                <label className="form-label">기도 종류</label>
                <Form.Select
                  value={prayType}
                  onChange={(e) => setPrayType(e.target.value)}
                  style={{ borderRadius: "100px" }}
                >
                  <option value="가족화목">가족화목</option>
                  <option value="건강기원">건강기원</option>
                  <option value="사업번창">사업번창</option>
                  <option value="소원성취">소원성취</option>
                </Form.Select>
              </div>

              {/* 기도 받는 사람 정보 */}
              <div className="mb-4">
                <label className="form-label">기도 받는 사람 정보</label>
                <Form.Control
                  type="text"
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                  placeholder="예: 홍길동, 가족 모두의 건강을 기원합니다."
                  style={{ borderRadius: "100px" }}
                />
              </div>

              {/* 기도 날짜 선택 */}
              <div className="mb-4">
                <label className="form-label">기도 날짜</label>
                <Form.Control
                  type="date"
                  value={prayDate.toISOString().split("T")[0]}
                  onChange={(e) => setPrayDate(new Date(e.target.value))}
                  style={{ borderRadius: "100px" }}
                />
              </div>

              {/* 가격 요약 */}
              <div className="py-3 border-top border-bottom">
                <div className="d-flex justify-content-between">
                  <span>상품 금액</span>
                  <span>{formatCurrency(calculateSubtotal())}원</span>
                </div>
              </div>

              {/* 최종 금액 */}
              <div className="d-flex justify-content-between align-items-center py-3">
                <span className="h5 mb-0">총 결제금액</span>
                <span
                  className="h4 mb-0 fw-bold"
                  style={{ color: "var(--main-color)" }}
                >
                  {formatCurrency(calculateTotal())}원
                </span>
              </div>

              {/* 약관 동의 */}
              <Form.Check
                type="checkbox"
                id="agree-terms"
                label="주문 내용을 확인하였으며, 결제 진행에 동의합니다."
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mb-4"
              />

              {/* 결제하기 버튼 */}
              <div className="d-grid">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={loading || cartItems.length === 0}
                  style={{
                    backgroundColor: "var(--main-color)",
                    borderColor: "var(--main-color)",
                    borderRadius: "100px",
                  }}
                >
                  {loading ? "처리 중..." : "결제하기"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  cartItems: state.cart.items,
});

const mapDispatchToProps = {
  updateCart,
  removeFromCart,
  clearCart,
};

export default connect(mapStateToProps, mapDispatchToProps)(CartPage);
